/*
 * What a list holds, by name, and the words that count it, kept apart from EmptyState so they can be
 * checked without a browser.
 *
 * A table's listing counts its rows with `nouns`, and one that shares its page with another table
 * names its view's parameters under `prefix`, so each keeps its own filters, sort and page in the
 * one URL.
 */

/** The listings a DataTable shows. Each has nouns to count its rows by. */
export type TableListing =
  | "members"
  | "agent-models"
  | "runs"
  | "members-by-spend"
  | "projects"
  | "tracker-projects"
  | "change-requests"

/** The listings a Member can filter, each with a case for when the filters match nothing. */
type Filterable = TableListing | "board" | "epics" | "my-tickets"

/** A filterable listing whose filters match nothing. */
type Filtered = `${Filterable}-filtered`

/**
 * Every list the web app shows, by name: empty because nothing is in it yet, or `…-filtered`
 * because the filters chosen match nothing.
 */
export type Listing =
  | TableListing
  | "my-tickets"
  | "my-tickets-all"
  | "board-new"
  | "epics"
  | "epic-tickets"
  | "groups"
  | "slack-rules"
  | "spend-day"
  | "activity-none"
  | "ticket-comments"
  | "slack-user-groups"
  | "adopted-projects"
  | "tracker-grants"
  | "github-installations"
  | "linked-repos"
  | "areas"
  | "labels"
  | "reachable-projects"
  | "connected-agents"
  | "access-tokens"
  | Filtered

/** What a filterable listing holds, as a sentence says it: "No tickets match these filters". */
const nouns: Record<Filterable, { one: string; many: string; prefix?: string }> = {
  members: { one: "Member", many: "Members" },
  "agent-models": { one: "model", many: "models", prefix: "models" },
  runs: { one: "run", many: "runs", prefix: "runs" },
  "members-by-spend": { one: "Member", many: "Members", prefix: "members" },
  projects: { one: "Project", many: "Projects" },
  "tracker-projects": { one: "project", many: "projects" },
  "change-requests": { one: "change request", many: "change requests" },
  board: { one: "ticket", many: "tickets" },
  epics: { one: "Epic", many: "Epics" },
  "my-tickets": { one: "ticket", many: "tickets" },
}

/** A table's listing's nouns, and the prefix its view's URL parameters go under, if any. */
export function tableListingOf(listing: TableListing) {
  return nouns[listing]
}

/** A count of what a listing holds: "1 Member", "68 Members". */
export function countOf(n: number, listing: Filterable) {
  const { one, many } = nouns[listing]
  return `${n} ${n === 1 ? one : many}`
}

/** Whether a listing is empty because its filters match nothing, rather than holding nothing yet. */
export function isFiltered(listing: Listing): listing is Filtered {
  return listing.endsWith("-filtered")
}

/** What a filtered listing says when its filters match nothing: "No tickets match these filters". */
export function filteredTitleOf(listing: Filtered) {
  const filterable = listing.slice(0, -"-filtered".length) as Filterable
  return `No ${nouns[filterable].many} match these filters`
}
